/**
 * How large a captured photo may be, in pixels.
 *
 * iOS Safari throws "Unable to complete previous operation due to low memory"
 * while decoding a 12 MP camera photo into a 4000×3000 canvas (about 48 MB,
 * and Safari keeps that backing store). The iOS box is a square so a 4:3
 * frame still clears the 1600×1300 minimum in either orientation:
 * 2000×1500 would shrink a portrait shot to 1125×1500 and the dimensions
 * check would reject it. 2400×2400 keeps landscape at 2400×1800 and portrait
 * at 1800×2400, and the canvas stays under 24 MB.
 *
 * `deviceMemory` is missing on every iPhone, and also on desktop Firefox, so
 * a missing value is not treated as a low-memory device.
 */

export interface CaptureBox {
  maxWidth: number;
  maxHeight: number;
}

/** Longest edge that still clears MIN_WIDTH × MIN_HEIGHT for a 4:3 photo held either way. */
export const IOS_CAPTURE_BOX: CaptureBox = { maxWidth: 2400, maxHeight: 2400 };

const FULL_BOX: CaptureBox = { maxWidth: 4000, maxHeight: 3000 };
const MID_BOX: CaptureBox = { maxWidth: 3000, maxHeight: 2250 };

const IOS_UA = /iPhone|iPod|iPad/i;

/**
 * iPhone, iPod, iPad, and iPadOS Safari, which sends a desktop Macintosh UA.
 * Same Macintosh + maxTouchPoints rule as isMobileDevice.
 */
export function isIosDevice(userAgent: string, maxTouchPoints: number): boolean {
  if (IOS_UA.test(userAgent)) return true;
  return /Macintosh/i.test(userAgent) && maxTouchPoints > 1;
}

export function captureLimits(deviceMemory: number | undefined, userAgent: string, maxTouchPoints: number): CaptureBox {
  if (isIosDevice(userAgent, maxTouchPoints)) return IOS_CAPTURE_BOX;
  if (deviceMemory !== undefined && deviceMemory < 2) return IOS_CAPTURE_BOX;
  if (deviceMemory !== undefined && deviceMemory < 4) return MID_BOX;
  return FULL_BOX;
}
